import axios from "@axios";

const DEFAULT_STATE = {
  topic: "",
  siteConnectors: [],
  sources: [],
  scrapedSources: {},
  sites: [],
  selectedSite: "closer",
  models: [],
  selectedModel: {
    name: "gpt-4o",
    temperature: 0.4,
    top_p: 0.9,
    verbosity: "medium",
    reasoning_effort: "medium",
  },
  prompts: [],
  sandboxProcesses: [],
  selectedProcessType: "via_angles",
  promptResults: {},
  isLoading: false,
  error: null,
  // Perplexity-related state
  currentTopicId: null,
  searchResults: [],

  // Posts by keywords :
  keywords: "",
  selectedSites: [],
  siteList: [],

  // Posts by sources :
  scoreMin: 8,

  // Full-auto:
  publicationStatus: "",
  customPromptTemplate: 0,
  customPromptTemplates: [],
  // Mode image (image_handler) des process qui partagent GenerationOptionsMenu.
  generationImageHandler: "generate",
  observationRange: 0,
  dagFrequency: 0,
};

export default {
  namespaced: true,

  state: {
    ...DEFAULT_STATE,
  },

  getters: {
    getTopic: (state) => state.topic,
    getSiteConnectors: (state) => state.siteConnectors,
    getSources: (state) => state.sources,
    getScrapedSources: (state) => state.scrapedSources,
    getSites: (state) => state.sites,
    getSelectedSite: (state) => state.selectedSite,
    getModels: (state) => state.models,
    getSelectedModel: (state) => state.selectedModel,
    getPrompts: (state) => state.prompts,
    getSandboxProcesses: (state) => state.sandboxProcesses,
    getSelectedProcessType: (state) => state.selectedProcessType,
    getPromptResults: (state) => state.promptResults,
    isLoading: (state) => state.isLoading,
    getError: (state) => state.error,
    getSourcesText: (state) => state.scrapedSources?.combined_text || "",
    getCurrentTopicId: (state) => state.currentTopicId,
    getSearchResults: (state) => state.searchResults,

    // Posts by keywords :
    getKeywords: (state) => state.keywords,
    getSiteList: (state) => state.siteList,
    getSelectedSites: (state) => state.selectedSites,

    // Posts by sources :
    getScoreMin: (state) => state.scoreMin,

    // Full-auto:
    getPublicationStatus: (state) => state.publicationStatus,
    getCustomPromptTemplate: (state) => state.customPromptTemplate,
    getCustomPromptTemplates: (state) => state.customPromptTemplates,
    getGenerationImageHandler: (state) => state.generationImageHandler,
    getObservationRange: (state) => state.observationRange,
    getDagFrequency: (state) => state.dagFrequency,
  },

  mutations: {
    INIT_STATE(state) {
      for (const [key, value] of Object.entries(DEFAULT_STATE)) {
        state[key] = value;
      }
    },

    SET_TOPIC(state, topic) {
      state.topic = topic;
    },

    SET_SITE_CONNECTORS(state, payload) {
      state.siteConnectors = payload;
    },

    SET_SOURCES(state, sources) {
      state.sources = sources;
    },

    ADD_SOURCE(state, source) {
      state.sources.push(source);
    },

    REMOVE_SOURCE(state, index) {
      state.sources.splice(index, 1);
    },

    SET_SCRAPED_SOURCES(state, data) {
      state.scrapedSources = data;
    },

    SET_SITES(state, sites) {
      state.sites = sites;
    },

    SET_SELECTED_SITE(state, site) {
      state.selectedSite = site;
    },

    SET_MODELS(state, models) {
      state.models = models;
    },

    SET_SELECTED_MODEL(state, model) {
      state.selectedModel = model;
    },

    UPDATE_MODEL_PARAMS(state, params) {
      state.selectedModel = { ...state.selectedModel, ...params };
    },

    SET_PROMPTS(state, prompts) {
      state.prompts = prompts;
    },

    SET_SANDBOX_PROCESSES(state, processes) {
      state.sandboxProcesses = processes;
    },

    SET_SELECTED_PROCESS_TYPE(state, processType) {
      state.selectedProcessType = processType;
    },

    SET_PROMPT_RESULT(state, { promptType, result }) {
      state.promptResults = {
        ...state.promptResults,
        [promptType]: result,
      };
    },

    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    CLEAR_RESULTS(state) {
      state.promptResults = {};
    },

    SET_CURRENT_TOPIC_ID(state, topicId) {
      state.currentTopicId = topicId;
    },

    SET_SEARCH_RESULTS(state, results) {
      state.searchResults = results;
    },

    // Posts by keywords :
    SET_KEYWORDS(state, payload) {
      state.keywords = payload;
    },
    SET_SITE_LIST(state, payload) {
      state.siteList = payload;
    },
    SET_SELECTED_SITES(state, payload) {
      state.selectedSites = payload;
    },

    // Posts by sources :
    SET_SCORE_MIN(state, payload) {
      state.scoreMin = payload;
    },

    // Full-auto:
    SET_PUBLICATION_STATUS(state, payload) {
      state.publicationStatus = payload;
    },
    SET_CUSTOM_PROMPT_TEMPLATE(state, payload) {
      state.customPromptTemplate = payload;
    },
    SET_CUSTOM_PROMPT_TEMPLATES(state, payload) {
      state.customPromptTemplates = payload;
    },
    SET_GENERATION_IMAGE_HANDLER(state, payload) {
      state.generationImageHandler = payload;
    },
    SET_OBSERVATION_RANGE(state, payload) {
      state.observationRange = payload;
    },
    SET_DAG_FREQUENCY(state, payload) {
      state.dagFrequency = payload;
    },
  },

  actions: {
    initState({ commit }) {
      commit("INIT_STATE");
    },

    // Nom standard, pour SiteFilter (commun à tous les stores)
    async getSiteConnectors({ commit }, params = {}) {
      try {
        // L'appel API est encapsulé ici
        const response = await axios.get("/api/connectors/", {
          params: {
            api_allowed: true, // On ne veut que les sites utilisables
            limit: 0, // Aucune limite, pour tout récupérer
            ...params,
          },
        });
        commit("SET_SITE_CONNECTORS", response.data.items);

        return response.data.items; // Retourne { total, items }
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Erreur API [fetchConnectors]:", error);
      }
    },

    async fetchModels({ commit }) {
      try {
        const response = await axios.get("/api/ai-factory/models");
        commit("SET_MODELS", response.data.models);
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error fetching models:", error);
      }
    },

    async fetchPrompts({ commit, state }) {
      try {
        const response = await axios.get("/api/ai-factory/prompts", {
          params: { process_type: state.selectedProcessType || "via_angles" },
        });
        commit("SET_PROMPTS", response.data.prompts);
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error fetching prompts:", error);
      }
    },

    async fetchSandboxProcesses({ commit }) {
      try {
        const response = await axios.get("/api/ai-factory/sandbox/processes");
        commit("SET_SANDBOX_PROCESSES", response.data.processes || []);
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error fetching sandbox processes:", error);
      }
    },

    async scrapeSources({ commit, state }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      try {
        // Extract URLs from sources (handle both string and object formats)
        const sourceUrls = state.sources
          .map((s) => (typeof s === "string" ? s : s.url))
          .filter((url) => url && url.trim());

        const response = await axios.post("/api/ai-factory/scrape-sources", {
          topic: state.topic,
          sources: sourceUrls,
        });

        commit("SET_SCRAPED_SOURCES", response.data);
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error scraping sources:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async testPrompt(
      { commit, state },
      {
        promptType,
        systemPrompt = null,
        userPrompt = null,
        modelConfig = null,
        additionalParams = {},
      }
    ) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      try {
        // Get sources text - prefer scraped combined text, but fall back to manual content if available
        let sourcesText = state.scrapedSources?.combined_text || "";

        // If no scraped sources, try to use manually entered content
        if (!sourcesText && state.sources.length > 0) {
          const manualContents = state.sources
            .filter((s) => s?.content)
            .map(
              (s, i) =>
                `<debut du contenu source #${i + 1}>\n${
                  s.content
                }\n<fin du contenu source #${i + 1}>`
            );

          if (manualContents.length > 0) {
            sourcesText = manualContents.join("\n\n\n\n");
          }
        }

        // Use provided modelConfig or fall back to state.selectedModel
        const effectiveModel = modelConfig || state.selectedModel;

        const requestData = {
          prompt_type: promptType,
          process_type: state.selectedProcessType || "via_angles",
          topic: state.topic,
          sources_text: sourcesText,
          model: effectiveModel.name,
          ...additionalParams,
        };

        if (effectiveModel.name?.startsWith("gemini")) {
          requestData.temperature = effectiveModel.temperature;
          requestData.top_p = effectiveModel.top_p;
          if (effectiveModel.top_k !== undefined) {
            requestData.top_k = effectiveModel.top_k;
          }
        } else if (effectiveModel.name === "gpt-4o") {
          requestData.temperature = effectiveModel.temperature;
          requestData.top_p = effectiveModel.top_p;
        } else if (effectiveModel.name?.startsWith("gpt-5")) {
          requestData.verbosity = effectiveModel.verbosity;
          requestData.reasoning_effort = effectiveModel.reasoning_effort;
        }

        if (systemPrompt) {
          requestData.system_prompt = systemPrompt;
        }
        if (userPrompt) {
          requestData.user_prompt = userPrompt;
        }

        // Pass results from previous stages based on prompt type
        if (
          promptType === "generate_content" ||
          promptType === "generate_extras"
        ) {
          // Both need the analyzed content from stage 1
          const analyzedContent = state.promptResults.analyze_sources?.result;
          if (analyzedContent) {
            requestData.analyzed_content = analyzedContent;
          }
        }

        if (
          promptType === "generate_extras" ||
          promptType === "generate_title" ||
          promptType === "generate_image"
        ) {
          // Stage 3 and stage 4 need the generated content from stage 2
          const postContent = state.promptResults.generate_content?.result;
          if (postContent) {
            requestData.post_content = postContent;
          }
        }

        const response = await axios.post(
          "/api/ai-factory/test-prompt",
          requestData
        );

        commit("SET_PROMPT_RESULT", {
          promptType,
          result: response.data,
        });

        return response.data;
      } catch (error) {
        const missingFields =
          error?.response?.data?.detail?.missing_fields || [];
        const serverMessage = error?.response?.data?.detail?.message;
        const formattedMessage =
          missingFields.length > 0
            ? `Missing required fields: ${missingFields.join(", ")}`
            : serverMessage || error.message;

        commit("SET_ERROR", formattedMessage);
        console.error("Error testing prompt:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    updateTopic({ commit }, topic) {
      commit("SET_TOPIC", topic);
    },

    updateSources({ commit }, sources) {
      commit("SET_SOURCES", sources);
    },

    addSource({ commit }, source) {
      commit("ADD_SOURCE", source);
    },

    removeSource({ commit }, index) {
      commit("REMOVE_SOURCE", index);
    },

    updateSelectedSite({ commit }, site) {
      commit("SET_SELECTED_SITE", site);
    },

    updateModelParams({ commit }, params) {
      commit("UPDATE_MODEL_PARAMS", params);
    },

    async updateSelectedProcessType({ commit, dispatch }, processType) {
      commit("SET_SELECTED_PROCESS_TYPE", processType || "via_angles");
      commit("CLEAR_RESULTS");
      await dispatch("fetchPrompts");
    },

    clearResults({ commit }) {
      commit("CLEAR_RESULTS");
    },

    async publishToWordPress({ commit, state }, { siteId, status = "draft" }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      try {
        // Get required content from prompt results
        // Generate_extras returns JSON with all metadata

        const title = state.promptResults.generate_title?.result;
        const contentResult = state.promptResults.generate_content?.result;

        const extrasResult = state.promptResults.generate_extras?.result;

        let excerpt, metaDescription, summaryBlock;

        if (extrasResult) {
          // Extract from generate_extras JSON result
          const extras =
            typeof extrasResult === "string"
              ? JSON.parse(extrasResult)
              : extrasResult;

          excerpt = extras.excerpt;
          metaDescription = extras.meta_description;
          summaryBlock = {
            bullet_point_1: extras.summary_bloc_bullet_point_1,
            bullet_point_2: extras.summary_bloc_bullet_point_2,
            bullet_point_3: extras.summary_bloc_bullet_point_3,
          };
        }

        const content = contentResult || state.promptResults.post_content?.result;

        if (!title || !content || !excerpt || !metaDescription) {
          throw new Error(
            "Missing required content. Please generate all article elements first."
          );
        }

        const imageResult = state.promptResults.generate_image?.result;
        const featuredImageB64 =
          imageResult && typeof imageResult === "object"
            ? imageResult.image_b64 || null
            : null;
        const featuredImageMime =
          imageResult && typeof imageResult === "object"
            ? imageResult.mime_type || "image/png"
            : null;

        // Parse summary block if it's a JSON string
        let parsedSummaryBlock = null;
        if (summaryBlock) {
          try {
            parsedSummaryBlock =
              typeof summaryBlock === "string"
                ? JSON.parse(summaryBlock)
                : summaryBlock;
          } catch (e) {
            console.warn("Failed to parse summary block:", e);
          }
        }

        const response = await axios.post(
          "/api/ai-factory/publish-to-wordpress",
          {
            site_id: siteId,
            title,
            content,
            excerpt,
            meta_description: metaDescription,
            summary_block: parsedSummaryBlock,
            status,
            featured_image_b64: featuredImageB64,
            featured_image_mime: featuredImageMime,
            featured_image_title: title,
          }
        );

        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error publishing to WordPress:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    setCurrentTopicId({ commit }, topicId) {
      commit("SET_CURRENT_TOPIC_ID", topicId);
    },

    async createTopic({ commit, state }) {
      try {
        const response = await axios.post("/api/ai-factory/topics", {
          title: state.topic,
          site_id:
            state.sites.find((s) => s.key === state.selectedSite)?.id || null,
        });
        commit("SET_CURRENT_TOPIC_ID", response.data.id);
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.message);
        throw error;
      }
    },

    // Posts by keywords :
    async getSiteList({ commit }) {
      const {
        data: { items },
      } = await axios.get("/sites/by-group-name/", {
        params: { group_name: "IA WordPress" },
      });
      commit("SET_SITE_LIST", items);

      return items;
    },
    updateKeywords({ commit }, payload) {
      commit("SET_KEYWORDS", payload);
    },
    updateSites({ commit }, payload) {
      commit("SET_SELECTED_SITES", payload);
    },

    // Posts by sources :
    updateScoreMin({ commit }, payload) {
      commit("SET_SCORE_MIN", payload);
    },

    // Full-auto:
    updatePublicationStatus({ commit }, payload) {
      commit("SET_PUBLICATION_STATUS", payload);
    },
    updateCustomPromptTemplate({ commit }, payload) {
      commit("SET_CUSTOM_PROMPT_TEMPLATE", payload);
    },
    updateCustomPromptTemplates({ commit }, payload) {
      commit("SET_CUSTOM_PROMPT_TEMPLATES", payload);
    },
    updateGenerationImageHandler({ commit }, payload) {
      commit("SET_GENERATION_IMAGE_HANDLER", payload);
    },
    updateObservationRange({ commit }, payload) {
      commit("SET_OBSERVATION_RANGE", payload);
    },
    updateDagFrequency({ commit }, payload) {
      commit("SET_DAG_FREQUENCY", payload);
    },
  },
};
